import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import {
  DropDownListComponent,
  FilteringEventArgs,
} from '@syncfusion/ej2-angular-dropdowns';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { UserDropdownDTO } from '@interfaces/user-dropdown-list';
import { UserService } from '@services/user.service';

interface UserGroupSearchRow extends UserDropdownDTO {
  value: number;
  text: string;
  source: string;
}

/**
 * User/group picker. The list for the organization is loaded when the popup is opened and the
 * search box filters those loaded records locally (from the first character) on name, email,
 * organization name and source. An empty search box shows every record. Shows a
 * Name | Email | Organization Name | Source table. The selected user id is written to
 * formGroup[fieldControlName], and the full row is emitted through userSelected.
 */
@Component({
  selector: 'app-user-group-search-select',
  templateUrl: './user-group-search-select.component.html',
  styleUrls: ['./user-group-search-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserGroupSearchSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() fieldControlName = 'selectUserOrGroup';
  @Input() placeholder = 'TankSetup.UserGroupPicker.SelectUserOrGroup';
  /** Omit to use the user's currently selected organization. */
  @Input() orgId?: number;
  @Input() includeChildOrgs = true;
  @Input() includeGroups = true;
  /** Narrow the shared API response to the selected organization, including its caller row. */
  @Input() restrictToOrganization = false;
  @Input() disabled = false;
  /** Rows to hide, e.g. users that are already in the list. */
  @Input() exclude: (user: UserDropdownDTO) => boolean = () => false;
  @Output() userSelected = new EventEmitter<UserDropdownDTO | null>();

  @ViewChild('list') list: DropDownListComponent;

  rows: UserGroupSearchRow[] = [];
  readonly fields = { text: 'text', value: 'value' };
  loading = false;

  private allUsers: UserDropdownDTO[] = [];
  private loadedKey: string | null = null;
  private currentTerm = '';
  private lastArgs: FilteringEventArgs | null = null;
  private destroy$ = new Subject<void>();
  private loadSubscription?: Subscription;

  constructor(
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  get control(): FormControl<number> {
    return this.formGroup?.get(this.fieldControlName) as FormControl<number>;
  }

  get enabled(): boolean {
    return !this.disabled && (!this.restrictToOrganization || this.orgId > 0);
  }

  ngOnInit(): void {
    if (this.formGroup && !this.control) {
      this.formGroup.addControl(this.fieldControlName, new FormControl(0));
    }

    // reset from the parent (setValue(0)) must clear the picker
    this.control?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((v) => {
      if (!v && this.list) {
        this.list.value = null;
        this.cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.orgId || changes.includeChildOrgs || changes.includeGroups || changes.restrictToOrganization) {
      this.loadSubscription?.unsubscribe();
      this.loadedKey = null;
      this.allUsers = [];
      this.rows = [];
      this.loading = false;
      this.control?.setValue(0);
      this.userSelected.emit(null);
    }
  }

  onOpen() {
    if (!this.enabled) return;
    this.currentTerm = '';
    this.lastArgs = null;
    const includeChildOrgs = this.restrictToOrganization ? false : this.includeChildOrgs;
    const key = `${this.orgId}|${includeChildOrgs}|${this.includeGroups}|${this.restrictToOrganization}`;
    if (key === this.loadedKey) {
      this.rows = this.filterRows('');
      this.cdr.markForCheck();
      return;
    }

    this.loadSubscription?.unsubscribe();
    this.loading = true;
    this.rows = [];
    this.cdr.markForCheck();
    this.loadSubscription = this.userService
      .getUserDropdownList(this.orgId, includeChildOrgs, this.includeGroups)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.allUsers = data ?? [];
          this.loadedKey = key;
          this.loading = false;
          this.showCurrentTerm();
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  onFiltering(args: FilteringEventArgs) {
    args.preventDefaultAction = true;
    this.currentTerm = (args.text ?? '').trim();
    this.lastArgs = args;
    if (this.loading) return; // shown once the load completes
    args.updateData(this.filterRows(this.currentTerm) as any);
  }

  onChange(args: { value: number | null; itemData?: UserGroupSearchRow }) {
    if (this.restrictToOrganization && args?.itemData && args.itemData.organizationId !== Number(this.orgId)) {
      this.control?.setValue(0);
      this.userSelected.emit(null);
      return;
    }
    const id = args?.value ?? 0;
    this.control?.setValue(id);
    this.userSelected.emit(args?.itemData ?? null);
  }

  private showCurrentTerm() {
    const filtered = this.filterRows(this.currentTerm);
    if (this.currentTerm && this.lastArgs) {
      this.lastArgs.updateData(filtered as any);
    } else {
      this.rows = filtered;
    }
    this.cdr.markForCheck();
  }

  private filterRows(term: string): UserGroupSearchRow[] {
    const t = term.toLowerCase();
    const matches = (u: UserDropdownDTO) => {
      if (!t) return true;
      const source = u.isGroup ? 'Group' : 'User';
      const sourceKey = `TankSetup.UserGroupPicker.${source}`;
      const translatedSource = this.translate.instant(sourceKey);
      return [this.displayName(u), u.name, u.emailAddress, u.organizationName,
        source, translatedSource === sourceKey ? '' : translatedSource].some((v) =>
        (v ?? '').toLowerCase().includes(t),
      );
    };

    return this.allUsers
      .filter((u) => !this.restrictToOrganization || u.organizationId === Number(this.orgId))
      .filter((u) => !this.exclude(u) && matches(u))
      .map((u) => ({
        ...u,
        name: this.displayName(u),
        value: u.id,
        text: this.displayName(u),
        source: u.isGroup ? 'Group' : 'User',
      }));
  }

  private displayName(user: UserDropdownDTO): string {
    return user.isGroup ? user.name :
      [user.firstName, user.lastName].map(part => (part ?? '').trim()).filter(Boolean).join(' ') || user.name;
  }
}
